import { useQuery } from '@tanstack/react-query';
import {
  Sparkles, UserPlus, UserMinus, Wrench, AlertOctagon, Ban, FileText, Clock,
  Loader2, ExternalLink, History,
} from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import { getHistorialActivo, buildPdfUrl, buildImagenUrl } from '@/services/api/inventario.api';
import { cn } from '@/lib/utils';
import type { AccionHistorial, HistorialEvento, ImagenEntrega } from '@/types/dominio';

interface Props {
  idItemMaestro: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Mapping visual de cada tipo de acción del historial.
 *
 * Decisión cromática: usamos la paleta semántica corporativa para que el
 * tipo de evento se reconozca en milisegundos sin tener que leer el texto:
 *   - Mantenimientos → azul (primary), porque son el flujo principal del módulo
 *   - Asignación → verde, conserva acción positiva
 *   - Devolución → ámbar, neutral pero distinguible de la asignación
 *   - Baja → rojo, severidad alta
 *   - Creación → gris, evento de inicialización
 */
const ACCION_META: Record<AccionHistorial, {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  dot: string;      // color del punto del timeline
  iconBg: string;   // fondo de la pastilla del icono
  badge: 'ok' | 'warn' | 'danger' | 'info' | 'secondary' | 'outline';
}> = {
  CREADO: {
    label: 'Activo creado',
    icon: Sparkles,
    dot: 'bg-slate-400',
    iconBg: 'bg-slate-100 text-slate-700 ring-slate-200',
    badge: 'secondary',
  },
  ASIGNADO: {
    label: 'Asignado a colaborador',
    icon: UserPlus,
    dot: 'bg-emerald-500',
    iconBg: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    badge: 'ok',
  },
  DEVUELTO: {
    label: 'Devuelto a bodega',
    icon: UserMinus,
    dot: 'bg-amber-500',
    iconBg: 'bg-amber-50 text-amber-800 ring-amber-200',
    badge: 'warn',
  },
  MANTENIMIENTO_PREVENTIVO: {
    label: 'Mantenimiento preventivo',
    icon: Wrench,
    dot: 'bg-primary-600',
    iconBg: 'bg-primary-50 text-primary-700 ring-primary-200',
    badge: 'info',
  },
  MANTENIMIENTO_CORRECTIVO: {
    label: 'Mantenimiento correctivo',
    icon: AlertOctagon,
    dot: 'bg-primary-700',
    iconBg: 'bg-primary-50 text-primary-800 ring-primary-200',
    badge: 'info',
  },
  DADO_DE_BAJA: {
    label: 'Dado de baja',
    icon: Ban,
    dot: 'bg-red-500',
    iconBg: 'bg-red-50 text-red-700 ring-red-200',
    badge: 'danger',
  },
};

export function HistorialEquipoDialog({ idItemMaestro, open, onOpenChange }: Props) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['historial-activo', idItemMaestro],
    queryFn: () => getHistorialActivo(idItemMaestro!),
    // Importante: solo dispara la query cuando el modal está abierto Y tenemos id.
    // Esto evita pegarle al backend cada vez que el padre re-renderea.
    enabled: open && !!idItemMaestro,
    // El historial cambia poco; 30s es suficiente para refrescar tras una mutación.
    staleTime: 30_000,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary-50 text-primary-700">
              <History className="h-4 w-4" />
            </span>
            Línea de tiempo del activo
          </DialogTitle>
          <DialogDescription>
            Recorrido cronológico del ciclo de vida del equipo, desde lo más reciente.
          </DialogDescription>
        </DialogHeader>

        {/* ── Cabecera del activo ──────────────────────────────────── */}
        {data?.activo && (
          <div className="rounded-lg border bg-muted/30 p-3 text-sm grid grid-cols-2 gap-x-4 gap-y-1.5">
            <div className="flex justify-between gap-3 col-span-2">
              <span className="text-muted-foreground">Serial</span>
              <span className="font-mono font-semibold">{data.activo.serial_global}</span>
            </div>
            {data.activo.nombre_glpi && (
              <div className="flex justify-between gap-3 col-span-2">
                <span className="text-muted-foreground">Activo</span>
                <span className="font-medium text-right">{data.activo.nombre_glpi}</span>
              </div>
            )}
            {data.activo.tipo && (
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Tipo</span>
                <span className="capitalize">{data.activo.tipo.toLowerCase()}</span>
              </div>
            )}
            {data.activo.marca && (
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Marca</span>
                <span>{data.activo.marca}</span>
              </div>
            )}
            {data.activo.empleado_actual && (
              <div className="flex justify-between gap-3 col-span-2">
                <span className="text-muted-foreground">Asignado actualmente a</span>
                <span className="font-medium truncate max-w-[300px] text-right">
                  {data.activo.empleado_actual.nombres}
                </span>
              </div>
            )}
            {!data.activo.empleado_actual && data.activo.espacio_actual && (
              <div className="flex justify-between gap-3 col-span-2">
                <span className="text-muted-foreground">Ubicado actualmente en</span>
                <span className="font-medium truncate max-w-[300px] text-right">
                  {data.activo.espacio_actual.nombre}
                </span>
              </div>
            )}
          </div>
        )}

        {/* ── Timeline ─────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto -mx-6 px-6 pt-1">
          {isLoading && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-12">
              <Loader2 className="h-4 w-4 animate-spin" />
              Cargando historial…
            </div>
          )}

          {isError && (
            <div className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-md p-3">
              No se pudo cargar el historial.{' '}
              {error instanceof Error ? error.message : ''}
            </div>
          )}

          {!isLoading && !isError && data?.eventos.length === 0 && (
            <div className="text-center py-12">
              <Clock className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
              <p className="text-sm text-muted-foreground">
                Este activo aún no tiene eventos registrados.
              </p>
            </div>
          )}

          {!isLoading && data && data.eventos.length > 0 && (
            <ol className="relative ml-4 border-l border-dashed border-border space-y-5 py-2">
              {data.eventos.map((ev) => (
                <TimelineItem key={ev.id_historial} evento={ev} />
              ))}
            </ol>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Item individual del timeline ──────────────────────────────────────────

function TimelineItem({ evento }: { evento: HistorialEvento }) {
  const meta = ACCION_META[evento.accion] ?? {
    label: evento.accion,
    icon: FileText,
    dot: 'bg-slate-400',
    iconBg: 'bg-slate-100 text-slate-700 ring-slate-200',
    badge: 'secondary' as const,
  };
  const Icon = meta.icon;

  // Un ASIGNADO/DEVUELTO hacia un espacio físico (activos de Compras sin
  // empleado) no tiene "colaborador" — el label por defecto dice eso, así
  // que lo reemplazamos por el nombre del espacio cuando aplica.
  const asignadoAEspacio =
    !evento.empleado_involucrado && evento.espacio_involucrado;
  const label = asignadoAEspacio
    ? `${evento.accion === 'DEVUELTO' ? 'Devuelto desde' : 'Asignado a'} ${evento.espacio_involucrado!.nombre}`
    : meta.label;

  return (
    <li className="relative pl-6">
      {/* Punto de la línea (sobre el border-l) */}
      <span
        className={cn(
          'absolute -left-[7px] top-1.5 h-3 w-3 rounded-full ring-4 ring-background',
          meta.dot,
        )}
        aria-hidden
      />

      <div className="space-y-2">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'inline-flex h-7 w-7 items-center justify-center rounded-md ring-1',
                meta.iconBg,
              )}
            >
              <Icon className="h-3.5 w-3.5" />
            </span>
            <div className="leading-tight">
              <p className="text-sm font-semibold text-foreground">{label}</p>
              <p className="text-xs text-muted-foreground tabular">
                {formatFechaHora(evento.fecha_hora)}
              </p>
            </div>
          </div>

          {evento.empleado_involucrado ? (
            <Badge variant="outline" className="font-normal">
              {evento.empleado_involucrado.nombres}
            </Badge>
          ) : evento.espacio_involucrado ? (
            <Badge variant="outline" className="font-normal">
              {evento.espacio_involucrado.nombre}
            </Badge>
          ) : null}
        </div>

        {evento.observaciones && (
          <p className="text-sm text-foreground/80 bg-muted/40 rounded-md px-3 py-2 border whitespace-pre-wrap">
            {evento.observaciones}
          </p>
        )}

        {/* Galería de fotos (típicamente para eventos ASIGNADO) ─────────── */}
        {evento.imagenes_entrega?.length > 0 && (
          <GaleriaImagenes imagenes={evento.imagenes_entrega} />
        )}

        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {evento.registrado_por && (
            <span>
              Registrado por <span className="font-medium text-foreground">{evento.registrado_por}</span>
            </span>
          )}
          {evento.ruta_archivo_pdf && (
            <Button
              asChild
              size="sm"
              variant="outline"
              className="h-7 px-2 ml-auto"
            >
              <a
                href={buildPdfUrl(evento.ruta_archivo_pdf)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FileText className="h-3.5 w-3.5" />
                Ver comprobante PDF
                <ExternalLink className="h-3 w-3 opacity-60" />
              </a>
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

// ── Galería de imágenes con click-to-zoom ────────────────────────────────
// Decisión: en vez de implementar un lightbox completo, abrimos cada imagen
// en una pestaña nueva. Es accesible (clic derecho funciona), descargable
// y no requiere otro dialog encima del que ya está abierto.
function GaleriaImagenes({ imagenes }: { imagenes: ImagenEntrega[] }) {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
        <span className="inline-block h-1 w-1 rounded-full bg-muted-foreground/50" />
        Fotos de la entrega ({imagenes.length})
      </p>
      <div className="grid grid-cols-4 sm:grid-cols-5 gap-1.5">
        {imagenes.map((img) => {
          const url = buildImagenUrl(img.ruta_imagen);
          return (
            <a
              key={img.id_imagen}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative aspect-square rounded-md overflow-hidden border bg-muted hover:ring-2 hover:ring-primary-400 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              title={img.nombre_original ?? 'Ver imagen'}
              aria-label={img.nombre_original ?? 'Ver imagen'}
            >
              <img
                src={url}
                alt={img.nombre_original ?? 'Foto de entrega'}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
              />
              {/* Overlay hover sutil */}
              <span className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors" aria-hidden />
            </a>
          );
        })}
      </div>
    </div>
  );
}

function formatFechaHora(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
